import { z } from 'zod';
import {
  ContradictionSchema,
  FactSchema,
  FactThemeSchema,
  IsoDateSchema,
  PartySchema,
  SlugSchema,
} from './entities.js';
import { PieceNatureSchema } from './piece-nature.js';
import { ProcedureSchema } from './procedure.js';
import { PartyLinkSchema } from './party-link.js';
import { ChronologySchema } from './chronology.js';
import { WikiDocumentsBundleSchema } from './wiki-document.js';
import { ProbativeForceSchema } from './probative-force.js';
import { ProceduralSideSchema } from './procedural-side.js';
import { CaseWikiSurfaceCapabilitiesSchema } from './profile.js';
import { ProceduralActPhaseSchema } from './procedural-act.js';
import {
  ProceduralObservationKindSchema,
  ProceduralStatusConfidenceSchema,
  ProceduralStatusObservationSchema,
  ProceduralStatusSchema,
} from './procedural-status.js';

/**
 * Case Wiki — read-side bundle schemas.
 *
 * Le tableau de bord du dossier (legalworkspace) charge l'intégralité du wiki en
 * une seule requête HTTP : un endpoint composite (BFF) renvoie ce bundle, le
 * front en extrait toutes les vues (Aperçu / Parties / Chronologie / Argumentaire).
 *
 * Pourquoi un bundle plutôt que des endpoints REST par table :
 *   - tables fortement liées (facts ↔ parties ↔ documents ↔ procédures)
 *   - les compteurs et tris du tableau de bord ont besoin de tout en mémoire
 *   - 1 round-trip + 1 entrée TanStack Query > N round-trips parallèles
 *
 * Les futures pages détail (filtres, pagination) brancheront leurs propres
 * endpoints REST sur les mêmes méthodes du `CaseWikiRepository` sans dupliquer.
 */

// ---------------------------------------------------------------------------
// Bundle — sub-shapes
// ---------------------------------------------------------------------------

/** Subset of `cases` exposed by the dashboard endpoint. */
export const BundleCaseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  client_name: z.string().nullable(),
  case_summary: z.string().nullable(),
  case_summary_short: z.string().nullable(),
  status: z.string().nullable(),
  deadline: z.string().nullable(),
  procedure_type: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type BundleCase = z.infer<typeof BundleCaseSchema>;

/** Subset of `case_documents` exposed by the dashboard endpoint (only pieces referenced by facts). */
export const BundleDocumentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  wiki_slug: SlugSchema,
  piece_number: z.string().nullable(),
  piece_description: z.string().nullable(),
  document_category: z.string().nullable(),
  document_subcategory: z.string().nullable(),
  document_date: IsoDateSchema.nullable(),
  /**
   * Nature juridique de la pièce (axe orthogonal à `document_category`).
   * Renseignée par l'agent Ingest dans `finish_ingest`. Null tant que la
   * pièce n'a pas été ingérée ou si l'agent retourne une valeur invalide.
   * Cf. `piece-nature.ts` pour la taxonomie complète.
   */
  piece_nature: PieceNatureSchema.nullable(),
  /**
   * M068 — épinglage manuel par l'avocat. Default false.
   */
  user_pinned_as_key: z.boolean().default(false),
  /**
   * M068 — annotation libre de l'avocat (max 4000 chars).
   */
  user_note: z.string().max(4000).nullable().optional(),
  /**
   * M068 — force probante calculée par l'agent (cf. `probative-force.ts`).
   * Nullable jusqu'à ce que l'agent l'ait inférée.
   */
  probative_force: ProbativeForceSchema.nullable().optional(),
  /**
   * M069 / DEBT-131 — camp procédural (our_party / opposing_party / court /
   * neutral). Renseigné par l'agent Ingest sur les pièces de nature
   * procédurale. Nullable pour les pièces hors procédure ou ingérées
   * avant M069 (cf. DEBT-131 backfill via M071 reset).
   */
  procedural_side: ProceduralSideSchema.nullable().optional(),
});
// Type wiki-pur — utile si un consumer batch / export n'a pas besoin de
// pouvoir ouvrir la pièce (ex. génération markdown sans preview).
export type BundleDocumentWiki = z.infer<typeof BundleDocumentSchema>;

/**
 * Champs storage exposés au bundle wiki — séparés du schéma "wiki-pur"
 * (PR-E2d round 10, review-loop architecture finding).
 *
 * Le bundle wiki agrège deux responsabilités orthogonales : la projection
 * wiki d'un document (slug, importance, side…) et un pointeur vers le
 * storage Supabase pour permettre l'ouverture du `DocumentPreviewPanel`
 * sans fetch supplémentaire côté frontend.
 *
 * Le path seul n'est pas exploitable côté client : la signed URL est
 * délivrée par `storageService` à la demande, avec vérification cabinet
 * server-side (`storage.routes.ts` /signed-url, ownership check via le
 * `case_id` parsé du path).
 *
 * Optional / nullable : tolérance pour les vieux snapshots de bundle en
 * cache TanStack Query si l'utilisateur n'a pas rechargé après deploy.
 *
 * Note maintainers : si on doit étendre l'info storage exposée au bundle
 * (size, mime_type granulaire, etc.), enrichir CE schema — pas
 * `BundleDocumentSchema` directement, pour conserver la séparation.
 */
export const BundleDocumentStorageRefSchema = z.object({
  file_path: z.string().min(1).optional(),
  document_type: z.string().min(1).nullable().optional(),
});
export type BundleDocumentStorageRef = z.infer<typeof BundleDocumentStorageRefSchema>;

/**
 * Schéma canonique `BundleDocument` (export wire) = wiki + storage ref.
 *
 * Les consommateurs (frontend FactDetailPage / PartyDetailPage /
 * DashboardPieces, backend bundle builder) importent `BundleDocument` /
 * `BundleDocumentSchemaCanonical` — la séparation interne `wiki` vs
 * `storageRef` est invisible côté usage mais explicite côté maintenance.
 */
export const BundleDocumentSchemaCanonical = BundleDocumentSchema.merge(
  BundleDocumentStorageRefSchema,
);
export type BundleDocument = z.infer<typeof BundleDocumentSchemaCanonical>;

// La table `case_questions` (et les schémas Zod associés `QuestionKindSchema`,
// `BundleQuestionSchema`, etc.) ont été retirés lors de la refonte L-W-B du
// 2026-05-29. La migration 097 DROP la table en DB. Le besoin (signaler ce
// qui reste à clarifier) est couvert par le `litigation_review` du Reviewer
// V2 (case_wiki_documents type='litigation_review').

/**
 * Penal procedural status history exposed by the backend bundle. The root field
 * remains optional for backward compatibility with older payloads; slugs stay
 * on the wire while the DB stores UUID FKs.
 */
export const PartyProceduralStatusSchema = z
  .object({
    id: z.string().uuid(),
    case_id: z.string().uuid(),
    party_slug: SlugSchema,
    observation_kind: ProceduralObservationKindSchema,
    status: ProceduralStatusSchema,
    phase: ProceduralActPhaseSchema.nullable(),
    effective_date: IsoDateSchema.nullable(),
    source_fact_slug: SlugSchema.nullable(),
    source_piece_slug: SlugSchema.nullable(),
    source_quote: z.string().min(10).max(2000).nullable(),
    confidence: ProceduralStatusConfidenceSchema,
    created_source: z.literal('ingest'),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .superRefine((value, ctx) => {
    const pairResult = ProceduralStatusObservationSchema.safeParse({
      observation_kind: value.observation_kind,
      status: value.status,
    });
    if (!pairResult.success) {
      for (const issue of pairResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    if (value.source_fact_slug === null && value.source_piece_slug === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_fact_slug'],
        message:
          'source_fact_slug or source_piece_slug is required on a persisted procedural status observation',
      });
    }
  });
export type PartyProceduralStatus = z.infer<typeof PartyProceduralStatusSchema>;

// ---------------------------------------------------------------------------
// Bundle — root
// ---------------------------------------------------------------------------

/**
 * Full payload returned by `GET /api/cases/:caseId/wiki`.
 *
 * `facts` are pre-hydrated (parties + pieces_sources arrays embedded), so the
 * front rarely needs the standalone `factParties` / `factPieces` arrays — but
 * we still expose them for graph-style explorations (party→facts count, etc.).
 *
 * `documents` est restreint aux pièces réellement référencées par au moins un
 * fait — un dossier peut contenir des centaines de pièces, ne pas tout envoyer.
 */
export const CaseWikiBundleSchema = z.object({
  case: BundleCaseSchema,
  parties: z.array(PartySchema),
  facts: z.array(FactSchema),
  documents: z.array(BundleDocumentSchemaCanonical),
  contradictions: z.array(ContradictionSchema),
  /**
   * M068 — multi-procédures. Default `[]` pour backward compat.
   * Cf. `procedure.ts`.
   */
  procedures: z.array(ProcedureSchema).default([]),
  /**
   * M068 — liens typés entre parties (~150 link_types en 6 catégories).
   * Cf. `party-link.ts` + `party-link-types.ts`.
   */
  partyLinks: z.array(PartyLinkSchema).default([]),
  /**
   * M068 — chronologies persistées (procedural × N + material + correspondence
   * + custom). Cf. `chronology.ts`.
   */
  chronologies: z.array(ChronologySchema).default([]),
  /** Server-owned profile capabilities; optional for legacy bundles. */
  surface_capabilities: CaseWikiSurfaceCapabilitiesSchema.optional(),
  /**
   * M068 — latest version des wiki documents actifs exposés au frontend
   * (snapshot / litigation_review / nullities_admissibility). Les types
   * historiques (`principal_parties`, `phases`, `arguments`) ont été retirés
   * du bundle le 2026-07-03. Cf. `wiki-document.ts`.
   */
  wikiDocuments: WikiDocumentsBundleSchema.optional(),
  /**
   * Penal procedural status history hydrated from
   * `case_party_procedural_statuses`. Optional for backward compatibility with
   * older cached/API payloads.
   */
  partyProceduralStatuses: z.array(PartyProceduralStatusSchema).optional(),
  /**
   * M135 (#462) — thèmes sémantiques par dossier (`case_fact_themes`).
   * Default `[]` pour compat ascendante avec les bundles persistés avant M135.
   */
  themes: z.array(FactThemeSchema).default([]),
});
export type CaseWikiBundle = z.infer<typeof CaseWikiBundleSchema>;
